/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/no-unused-vars */
// TODO: Fix display name
// TODO: Fix unused vars

import { useCallback } from 'react';
import { FILTER_NAMES } from '../../../../components/filters/hooks/filter-names.constants';
import {
  AppTabularColumn,
  AppTabularColumnGroup,
} from '../../../../components/reports/dtos/app-tabular.dto';
import './scenario-planner-details-table.component.scss';
import { CellProps } from '../../../../components/reports/generic-table/cell-prod.component';
import {
  FilterOptionSelectedValue,
  FilterSelectedValue,
} from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import {
  MassEditBlock,
  MassEditField,
} from '../mass-edit-sidebar/mass-edit-sidebar.component';
import { ScenarioPlannerDetailsCellProdComponent } from './scenario-planner-details-cell-prod.component';
import { ScenarioPlannerInputCellReadOnlyComponent } from './scenario-planner-input-cell-read-only.component';
import { ScenarioPlannerInputCellComponent } from './scenario-planner-input-cell.component';
import { ScenarioPlannerInputHeaderComponent } from './scenario-planner-input-header.component';
import { ScenarioPlannerNumberCellComponent } from './scenario-planner-number-cell.component';
import {
  ScenarioPlannerTableComponentProps,
  ScenarioPlannerTableHelper,
} from './scenario-planner-table.helper';
import { TableInputComponent } from './table-input.component';

export interface ScenarioPlannerTabularColumn extends AppTabularColumn {
  isInput?: boolean;
  readOnly?: boolean;
  inputTypeId?: string;
  hasValues?: boolean; // Added to track if column has values from API
}

export interface ExtendedCellProps extends CellProps {
  col: ScenarioPlannerTabularColumn;
  inputTypeOptions?: FilterOptionSelectedValue[];
  onRunEdit?: (
    massEditBlock: MassEditBlock[],
    filterSelection: FilterSelectedValue[],
  ) => Promise<any>;
  onRunEditMode?: (massEditField: MassEditField[]) => Promise<any>;
  onClearCell?: (data: any, col: ScenarioPlannerTabularColumn) => Promise<any>;
  onClearColumn?: (col: ScenarioPlannerTabularColumn) => Promise<any>; // Removed allData parameter
}

const CellComponent = ({
  data,
  col,
  inputTypeOptions,
  onRunEdit,
  onRunEditMode,
  onClearCell,
}: ExtendedCellProps) => {
  if (col.isInput) {
    if (col.readOnly) {
      return (
        <ScenarioPlannerInputCellReadOnlyComponent
          onRunEdit={onRunEdit}
          data={data}
          col={col}
          inputTypeOptions={inputTypeOptions}
        />
      );
    } else {
      return (
        <ScenarioPlannerInputCellComponent
          onRunEdit={onRunEdit}
          onClearCell={onClearCell}
          data={data}
          col={col}
          inputTypeOptions={inputTypeOptions}
        />
      );
    }
  } else {
    return <ScenarioPlannerNumberCellComponent data={data} col={col} />;
  }
};

const HeaderComponent = ({
  data,
  col,
  inputTypeOptions,
  onRunEdit,
  onRunEditMode,
  onClearColumn,
}: ExtendedCellProps) => {
  if (col.isInput) {
    if (!col.readOnly) {
      return (
        <ScenarioPlannerInputHeaderComponent
          data={data}
          col={col}
          inputTypeOptions={inputTypeOptions}
          onRunEditMode={onRunEditMode}
          onClearColumn={onClearColumn}
        />
      );
    }
  }
  return (
    <div className={`scenario-planner-input-header read-only ${col.id}`}>
      <div className="header-top-row">{col.name}</div>
    </div>
  );
};

const FooterCellComponent = ({ data, col }: ExtendedCellProps) => {
  if (col.isInput === true) {
    return <div>-</div>;
  } else {
    return <ScenarioPlannerNumberCellComponent data={data} col={col} />;
  }
};

interface ScenarioPlannerDetailsTableComponentProps
  extends ScenarioPlannerTableComponentProps {
  inputTypeOptions: FilterOptionSelectedValue[];
  onRunEdit: (
    massEditBlock: MassEditBlock[],
    filterSelection: FilterSelectedValue[],
  ) => Promise<any>;
  onRunEditMode: (massEditField: MassEditField[]) => Promise<any>;
  showEditIconOnHover?: boolean;
  onRowEditClick?: (rowData: any) => void;
}

export const ScenarioPlannerDetailsTableComponent = ({
  height = 500,
  onRunEditMode,
  onRunEdit,
  inputTypeOptions,
  idField,
  setInvalidateQuery,
  onRowSelectionChange,
  reportName,
  reportId,
  totalReportName,
  filterSelection,
  enabled,
  showEditIconOnHover = false,
  onRowEditClick,
}: ScenarioPlannerDetailsTableComponentProps) => {
  const parseColumns = useCallback(
    (columns: AppTabularColumn[]) => {
      const cellFactory =
        (col: AppTabularColumn) =>
        ({ row }: { row: any }) => {
          return col.descriptors === true && row.depth !== 0 ? (
            // Children
            <ScenarioPlannerDetailsCellProdComponent
              data={row}
              col={col}
              showEditIconOnHover={showEditIconOnHover}
              onRowEditClick={onRowEditClick}
            />
          ) : (
            // Parent
            <div className={'scenario-table-cell header-sortable-text '}>
              {row.original.category_name}
            </div>
          );
        };
      const getColSize = (col: any, index: number) => {
        if (index === 0) {
          return 450;
        } else {
          return 60;
        }
      };
      return ScenarioPlannerTableHelper.parseColumns(
        columns,
        cellFactory,
        getColSize,
      );
    },
    [onRowEditClick, showEditIconOnHover],
  );
  // Handler for clearing individual cells
  const handleClearCell = useCallback(
    async (data: any, col: ScenarioPlannerTabularColumn) => {
      const selectedValue: FilterSelectedValue = {
        id: FILTER_NAMES.PROD,
        values: [
          {
            label: data.name,
            value: data.id,
          },
        ],
      };
      const editBlock: MassEditBlock = {
        fieldId: col.idField || '',
        inputType: col.inputTypeId || 'detault',
        value: undefined, // Clear the value
      };

      return onRunEdit([editBlock], [selectedValue]);
    },
    [onRunEdit],
  );

  // Handler for clearing entire column
  const handleClearColumn = useCallback(
    async (col: ScenarioPlannerTabularColumn) => {
      if (!col.hasValues) {
        return;
      }

      // Create ONE edit block for the field being cleared
      const editBlocks: MassEditBlock[] = [
        {
          fieldId: col.idField || '',
          inputType: col.inputTypeId || 'default',
          value: undefined, // Clear the value
        },
      ];

      // Pass empty array for applyToFilterSelection to clear all data in the column
      const filterSelection: FilterSelectedValue[] = [];

      // Execute clear operation for entire column
      return onRunEdit(editBlocks, filterSelection);
    },
    [onRunEdit],
  );

  const parseMeasuresColumns = useCallback(
    (
      columns: AppTabularColumn[],
      data: any,
      total?: any,
      columnGroups?: AppTabularColumnGroup[],
    ) => {
      const cellFactory =
        (col: AppTabularColumn, index: number) =>
        ({ row }: any) =>
          // Children level
          row.depth !== 0 ? (
            <CellComponent
              onRunEdit={onRunEdit}
              onClearCell={handleClearCell}
              data={row.original}
              col={col}
              inputTypeOptions={inputTypeOptions}
            />
          ) : (
            // Parent level
            <div className={'scenario-table-cell header-sortable-text '}>
              {col.name}
            </div>
          );

      const headerFactory =
        (col: AppTabularColumn, index: number) => (props: any) => (
          <HeaderComponent
            onRunEditMode={onRunEditMode}
            onClearColumn={handleClearColumn}
            data={undefined}
            col={col}
            inputTypeOptions={inputTypeOptions}
          />
        );

      const footerFactory =
        (col: AppTabularColumn, index: number) => (props: any) => (
          <FooterCellComponent data={total} col={col} />
        );

      const getColSize = (col: any, index: number) => {
        if (col.isInput) {
          return 100;
        } else {
          if (col.id === 'DOLLAR_SALES') {
            return 120;
          } else {
            return 85;
          }
        }
      };
      return ScenarioPlannerTableHelper.parseGroupMeasuresColumns(
        columns,
        cellFactory,
        getColSize,
        data,
        total,
        footerFactory,
        headerFactory,
        columnGroups,
      );
    },
    [
      inputTypeOptions,
      onRunEdit,
      onRunEditMode,
      handleClearCell,
      handleClearColumn,
    ],
  );

  return (
    <TableInputComponent
      className="scenario-planner-details-table grouped-rows scenario-mass-edit-table"
      filterSelection={filterSelection}
      reportName={reportName}
      reportId={reportId}
      totalReportName={totalReportName}
      enabled={enabled}
      defaultSortBy={[{ id: 'name', desc: false }]}
      height={height}
      parseColumns={parseColumns}
      parseMeasuresColumns={parseMeasuresColumns}
      showTotal={false}
      idField={idField}
      onRowSelectionChange={onRowSelectionChange}
      enableRowSelection={true}
      enableMultiRowSelection={true}
      setInvalidateQuery={setInvalidateQuery}
      enableStickyFirstColumn={true}
      suppressRowClickSelection={true}
    />
  );
};
